/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MSC Partners Page — Interactions V2.1
 * Built from agents/msc-partners-design-spec.md
 * Vanilla JS · IIFE · No globals
 *
 * 1. Light follow (radial-gradient with brand_color)
 * 2. Expand overlay V2 (cinematic hero + parallax + multi-product + quotes)
 * 3. Keyboard a11y (Escape, Tab trap)
 * ═══════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  var ROOT_SEL = '.msc-partners';
  var CARD_SEL = '.msc-partners__card';
  var ACTIVE_CLS = 'is-active';

  var root = document.querySelector(ROOT_SEL);
  if (!root) return;

  var isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ═══════════════════════════════════════════════════════════════════════
     1. LIGHT FOLLOW — Radial gradient shine on mousemove
     ═══════════════════════════════════════════════════════════════════════ */

  function initLightFollow() {
    if (isTouch || reducedMotion) return;

    root.querySelectorAll(CARD_SEL).forEach(function (card) {
      var shine = card.querySelector('.msc-partners__card-shine');
      if (!shine) return;

      var brandColor = card.dataset.brandColor || '#C1282D';

      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var xPct = ((e.clientX - rect.left) / rect.width) * 100;
        var yPct = ((e.clientY - rect.top) / rect.height) * 100;
        shine.style.background =
          'radial-gradient(circle at ' + xPct + '% ' + yPct + '%, ' + brandColor + '26 0%, transparent 65%)';
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     2. EXPAND OVERLAY
     ═══════════════════════════════════════════════════════════════════════ */

  var overlay = null;
  var previousFocus = null;
  var activeCard = null;
  var liveRegion = null;
  var overlayObserver = null;

  function initExpand() {
    root.querySelectorAll(CARD_SEL + '[data-expand]').forEach(function (card) {
      card.setAttribute('aria-expanded', 'false');
      card.addEventListener('click', function (e) {
        if (e.target.closest('a[href]')) return;
        e.preventDefault();
        openOverlay(card);
      });

      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openOverlay(card);
        }
      });
    });
  }

  function openOverlay(card) {
    previousFocus = document.activeElement;
    activeCard = card;
    card.setAttribute('aria-expanded', 'true');
    var cardRect = card.getBoundingClientRect();

    var d = card.dataset;
    var name = d.name || '';
    var tier = d.tier || '';
    var tagline = d.tagline || '';
    var story = d.story || '';
    var heroImage = d.heroImage || '';
    var overlayImage = d.overlayImage || heroImage || '';
    var heroGradient = d.heroGradient || '';
    var url = d.url || '';
    var brandColor = d.brandColor || '#C1282D';
    var accentLight = d.accentLight || brandColor;
    var hqLocation = d.hqLocation || '';
    var partnerDesignation = d.partnerDesignation || '';
    var quoteText = d.quoteText || '';
    var quoteAuthor = d.quoteAuthor || '';
    var logo = d.logo || '';

    // Collect activation images
    var activations = [];
    for (var ai = 1; ai <= 4; ai++) {
      var aImg = d['activationImage' + ai] || '';
      var aLabel = d['activationLabel' + ai] || '';
      if (aImg) activations.push({ image: aImg, label: aLabel });
    }

    var productsLabel = d.productsLabel || 'Favorites';

    // Staff pick data
    var staffPick = d.staffPick === 'true';
    var staffPickName = d.staffPickName || '';
    var staffPickTitle = d.staffPickTitle || '';
    var staffPickQuote = d.staffPickQuote || '';
    var staffPickImage = d.staffPickImage || '';

    // Collect products — hero/feature get up to 3, supporter gets 1, associate gets 0
    var maxProducts = (tier === 'associate') ? 0 : (tier === 'supporter') ? 1 : 3;
    var products = [];
    for (var pi = 1; pi <= maxProducts; pi++) {
      var pName = d['product' + pi + 'Name'] || '';
      if (pName) {
        products.push({
          name: pName,
          detail: d['product' + pi + 'Detail'] || '',
          image: d['product' + pi + 'Image'] || '',
          tag: d['product' + pi + 'Tag'] || '',
          url: d['product' + pi + 'Url'] || ''
        });
      }
    }

    // ── Build overlay HTML ──
    var heroBg = heroGradient || 'linear-gradient(135deg, ' + brandColor + '15 0%, #0a0a0a 50%)';
    var heroClass = 'msc-partners__overlay-hero msc-partners__overlay-hero--' + tier;
    if (!overlayImage) heroClass += ' msc-partners__overlay-hero--no-image';

    var html = '';
    html += '<div class="' + heroClass + '">';
    if (!overlayImage) {
      // F1-inspired bold brand color background
      html += '<div class="msc-partners__overlay-hero-bg" data-parallax-bg style="background: linear-gradient(135deg, ' + brandColor + ' 0%, #0a0a0a 60%)"></div>';
      html += '<div class="msc-partners__overlay-hero-accent" style="background: linear-gradient(315deg, transparent 0%, ' + brandColor + '30 40%, ' + brandColor + '50 55%, transparent 75%)"></div>';
    } else {
      html += '<div class="msc-partners__overlay-hero-bg" data-parallax-bg style="background: ' + heroBg + '"></div>';
      html += '<img class="msc-partners__overlay-hero-img" src="' + escapeAttr(overlayImage) + '" alt="" fetchpriority="high">';
    }
    html += '<div class="msc-partners__overlay-hero-glow" data-parallax-glow style="background: radial-gradient(ellipse, ' + brandColor + '08 0%, transparent 70%)"></div>';
    html += '<div class="msc-partners__overlay-hero-grain"></div>';
    html += '<div class="msc-partners__overlay-hero-fade"></div>';
    html += '<button class="msc-partners__overlay-close" data-overlay-close aria-label="Close">\u2715</button>';
    html += '<div class="msc-partners__overlay-hero-info" data-parallax-info>';
    if (logo) {
      html += '<img class="msc-partners__overlay-hero-logo" src="' + escapeAttr(logo) + '" alt="" width="120" height="auto">';
    }
    html += '<h2 class="msc-partners__overlay-name" id="msc-overlay-name">' + escapeHtml(name) + '</h2>';
    if (tagline) {
      html += '<p class="msc-partners__overlay-tagline" style="color: ' + accentLight + '">' + escapeHtml(tagline) + '</p>';
    }
    html += '</div>';
    html += '<div class="msc-partners__overlay-scroll-hint" data-scroll-hint>';
    html += '<div class="msc-partners__overlay-scroll-hint-line"></div>';
    html += '</div>';
    html += '</div>';

    // Stats bar
    if (hqLocation || partnerDesignation) {
      html += '<div class="msc-partners__overlay-stats" data-stats-bar>';
      if (hqLocation) {
        html += '<div class="msc-partners__overlay-stat">';
        html += '<div class="msc-partners__overlay-stat-value">' + escapeHtml(hqLocation) + '</div>';
        html += '<div class="msc-partners__overlay-stat-label">Headquarters</div>';
        html += '</div>';
      }
      if (partnerDesignation) {
        html += '<div class="msc-partners__overlay-stat">';
        html += '<div class="msc-partners__overlay-stat-value">' + escapeHtml(partnerDesignation) + '</div>';
        html += '<div class="msc-partners__overlay-stat-label">Designation</div>';
        html += '</div>';
      }
      html += '</div>';
    }

    // Story body (skip for associate — they get minimal overlay)
    if (story && tier !== 'associate') {
      html += '<div class="msc-partners__overlay-body">';
      var paragraphs = story.split('|');
      for (var si = 0; si < paragraphs.length; si++) {
        var trimmed = paragraphs[si].replace(/^\s+|\s+$/g, '');
        if (trimmed) {
          var pContent = trimmed;
          // Bold the first sentence of the first paragraph
          if (si === 0) {
            var dotIdx = pContent.indexOf('.');
            if (dotIdx !== -1) {
              pContent = '<strong>' + pContent.substring(0, dotIdx + 1) + '</strong><br><br>' + pContent.substring(dotIdx + 1).replace(/^\s+/, '');
            }
          }
          html += '<p class="msc-partners__overlay-story" style="opacity: 0; transform: translateY(15px); transition: all 0.6s cubic-bezier(0.16,1,0.3,1) ' + (0.4 + si * 0.1) + 's">' + pContent + '</p>';
        }
      }
      html += '</div>';
    }

    // Staff Pick callout (not for associate)
    if (staffPick && staffPickQuote && tier !== 'associate') {
      html += '<div class="msc-partners__overlay-staff-pick">';
      if (staffPickImage) {
        html += '<div class="msc-partners__overlay-staff-pick-image">';
        html += '<img src="' + escapeAttr(staffPickImage) + '" alt="" loading="lazy">';
        html += '</div>';
      }
      html += '<div class="msc-partners__overlay-staff-pick-content">';
      html += '<div class="msc-partners__overlay-staff-pick-label">Staff Pick</div>';
      html += '<p class="msc-partners__overlay-staff-pick-quote">\u201C' + escapeHtml(staffPickQuote) + '\u201D</p>';
      if (staffPickName) {
        var authorLine = staffPickName;
        if (staffPickTitle) authorLine += ', ' + staffPickTitle;
        html += '<p class="msc-partners__overlay-staff-pick-author">\u2014 ' + escapeHtml(authorLine) + '</p>';
      }
      html += '</div>';
      html += '</div>';
    }

    // Activation Showcase (not for associate)
    if (activations.length > 0 && tier !== 'associate') {
      html += '<div class="msc-partners__overlay-activations">';
      html += '<div class="msc-partners__overlay-activations-label">Your Brand Across MSC</div>';
      html += '<div class="msc-partners__overlay-activations-grid">';
      for (var ak = 0; ak < activations.length; ak++) {
        html += '<div class="msc-partners__overlay-activation" style="opacity: 0; transform: translateY(15px); transition: all 0.6s cubic-bezier(0.16,1,0.3,1) ' + (0.5 + ak * 0.1) + 's">';
        html += '<div class="msc-partners__overlay-activation-img">';
        html += '<img src="' + escapeAttr(activations[ak].image) + '" alt="' + escapeAttr(activations[ak].label) + '" loading="lazy">';
        html += '</div>';
        html += '<div class="msc-partners__overlay-activation-name">' + escapeHtml(activations[ak].label) + '</div>';
        html += '</div>';
      }
      html += '</div>';
      html += '</div>';
    }

    // Products (hero, feature, supporter tiers)
    if (tier !== 'associate' && products.length > 0) {
      // Check if any product has an image
      var hasAnyImage = false;
      for (var pi2 = 0; pi2 < products.length; pi2++) {
        if (products[pi2].image) { hasAnyImage = true; break; }
      }
      var productsClass = 'msc-partners__overlay-products';
      if (!hasAnyImage) productsClass += ' msc-partners__overlay-products--icon-grid';
      html += '<div class="' + productsClass + '">';
      html += '<div class="msc-partners__overlay-products-label">' + escapeHtml(productsLabel) + '</div>';
      for (var j = 0; j < products.length; j++) {
        var p = products[j];
        var isPrimary = j === 0;
        var cardBg = 'linear-gradient(135deg, #0f0f0f 0%, #141414 100%)';

        if (!hasAnyImage) {
          // ── Icon-grid card (compact, no image) ──
          var iconSvg = '';
          var tagLower = (p.tag || '').toLowerCase();
          if (tagLower === 'build') {
            iconSvg = '<polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>';
          } else if (tagLower === 'platform') {
            iconSvg = '<path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>';
          } else if (tagLower === 'read') {
            iconSvg = '<path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>';
          } else {
            iconSvg = '<path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>';
          }

          var cardTag = p.url ? 'a' : 'div';
          var cardAttrs = p.url ? ' href="' + escapeAttr(p.url) + '" target="_blank" rel="noopener noreferrer"' : '';
          html += '<' + cardTag + cardAttrs + ' class="msc-partners__overlay-product msc-partners__overlay-product--icon-card" style="background: ' + cardBg + '; opacity: 0; transform: translateY(20px); transition: all 0.6s cubic-bezier(0.16,1,0.3,1) ' + (0.5 + j * 0.1) + 's">';
          html += '<div class="msc-partners__overlay-product-placeholder-icon" style="border-color: ' + brandColor + '30; background: ' + brandColor + '10">';
          html += '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="' + accentLight + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">';
          html += iconSvg;
          html += '</svg>';
          html += '</div>';
          html += '<div class="msc-partners__overlay-product-icon-card-text">';
          html += '<div class="msc-partners__overlay-product-name msc-partners__overlay-product-name--secondary">' + escapeHtml(p.name) + '</div>';
          if (p.detail) {
            html += '<div class="msc-partners__overlay-product-detail">' + escapeHtml(p.detail) + '</div>';
          }
          html += '</div>';
          if (p.tag) {
            html += '<div class="msc-partners__overlay-product-tag" style="color: rgba(255,255,255,0.35); background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.06)">' + escapeHtml(p.tag) + '</div>';
          }
          html += '</' + cardTag + '>';

        } else {
          // ── Standard image card ──
          var nameClass = isPrimary ? 'msc-partners__overlay-product-name--primary' : 'msc-partners__overlay-product-name--secondary';
          var sizeClass = isPrimary ? 'msc-partners__overlay-product--primary' : 'msc-partners__overlay-product--secondary';

          var cardTag = p.url ? 'a' : 'div';
          var cardAttrs = p.url ? ' href="' + escapeAttr(p.url) + '" target="_blank" rel="noopener noreferrer"' : '';
          html += '<' + cardTag + cardAttrs + ' class="msc-partners__overlay-product ' + sizeClass + '" style="background: ' + cardBg + '; opacity: 0; transform: translateY(20px); transition: all 0.6s cubic-bezier(0.16,1,0.3,1) ' + (0.5 + j * 0.1) + 's">';

          // Background image (fills entire card)
          if (p.image) {
            html += '<img class="msc-partners__overlay-product-bg" src="' + escapeAttr(p.image) + '" alt="' + escapeAttr(p.name) + '" loading="lazy">';
          } else {
            html += '<div class="msc-partners__overlay-product-placeholder">';
            html += '<div class="msc-partners__overlay-product-placeholder-icon" style="border-color: ' + brandColor + '30; background: ' + brandColor + '10">';
            html += '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="' + accentLight + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">';
            html += '<path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>';
            html += '</svg>';
            html += '</div>';
            html += '</div>';
          }
          // Bottom gradient for text readability
          html += '<div class="msc-partners__overlay-product-gradient"></div>';
          // Tag badge (top-right)
          if (p.tag) {
            var tagColor = 'rgba(255,255,255,0.35)';
            var tagBg = 'rgba(255,255,255,0.05)';
            var tagBorder = 'rgba(255,255,255,0.06)';
            html += '<div class="msc-partners__overlay-product-tag" style="color: ' + tagColor + '; background: ' + tagBg + '; border: 1px solid ' + tagBorder + '">' + escapeHtml(p.tag) + '</div>';
          }
          // Product info (bottom-left overlay on image)
          html += '<div class="msc-partners__overlay-product-info">';
          html += '<div class="msc-partners__overlay-product-name ' + nameClass + '">' + escapeHtml(p.name) + '</div>';
          if (p.detail) {
            html += '<div class="msc-partners__overlay-product-detail">' + escapeHtml(p.detail) + '</div>';
          }
          html += '</div>';
          html += '</' + cardTag + '>';
        }
      }
      html += '</div>';
    }

    // Quote (not for associate)
    if (quoteText && tier !== 'associate') {
      html += '<div class="msc-partners__overlay-quote" style="border-top: 1px solid ' + brandColor + '20">';
      html += '<p class="msc-partners__overlay-quote-text">\u201C' + escapeHtml(quoteText) + '\u201D</p>';
      html += '<p class="msc-partners__overlay-quote-author" style="color: ' + accentLight + '">\u2014 ' + escapeHtml(quoteAuthor) + '</p>';
      html += '</div>';
    }

    // CTA Bar
    if (url) {
      html += '<div class="msc-partners__overlay-cta">';
      html += '<a href="' + escapeAttr(url) + '" target="_blank" rel="noopener" class="msc-partners__overlay-cta-btn" style="background: ' + brandColor + '">';
      html += 'Visit ' + escapeHtml(name) + ' <span style="font-size: 16px; opacity: 0.8">\u2192</span>';
      html += '</a>';
      html += '<div class="msc-partners__overlay-cta-cobrand">MSC \u00D7 ' + escapeHtml(name) + ' \u00B7 2026</div>';
      html += '</div>';
    }

    // ── Build DOM ──
    overlay = document.createElement('div');
    overlay.className = 'msc-partners__overlay';
    overlay.setAttribute('data-partner', name);
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'msc-overlay-name');

    var panel = document.createElement('div');
    panel.className = 'msc-partners__overlay-panel';
    panel.setAttribute('role', 'document');
    panel.setAttribute('tabindex', '-1');
    panel.style.border = '1px solid ' + brandColor + '18';
    panel.style.setProperty('--panel-brand', brandColor);
    panel.innerHTML = html;

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Set transform-origin to expand from clicked card
    var cx = (cardRect.left + cardRect.width / 2) / window.innerWidth * 100;
    var cy = (cardRect.top + cardRect.height / 2) / window.innerHeight * 100;
    panel.style.transformOrigin = cx + '% ' + cy + '%';

    // Force reflow then animate in
    overlay.offsetHeight;
    overlay.classList.add(ACTIVE_CLS);

    // Deep-link: update URL hash
    var slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    if (history.replaceState) history.replaceState(null, '', '#partner-' + slug);

    // GA4 engagement tracking
    if (typeof gtag === 'function') {
      gtag('event', 'partner_detail_view', {
        partner_name: name, partner_tier: tier, event_category: 'sponsors'
      });
    }

    // Progressive reveal via IntersectionObserver
    overlayObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          overlayObserver.unobserve(entry.target);
        }
      });
    }, { root: panel, threshold: 0.1 });
    panel.querySelectorAll('.msc-partners__overlay-story, .msc-partners__overlay-product, .msc-partners__overlay-activation, .msc-partners__overlay-staff-pick').forEach(function (el) {
      overlayObserver.observe(el);
    });

    // ── Scroll-driven effects (rAF-gated) ──
    var ticking = false;
    var scrollHintHidden = false;
    var heroBgEl = panel.querySelector('[data-parallax-bg]');
    var heroGlowEl = panel.querySelector('[data-parallax-glow]');
    var heroInfoEl = panel.querySelector('[data-parallax-info]');
    var scrollHintEl = panel.querySelector('[data-scroll-hint]');

    panel.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var scrollTop = panel.scrollTop;
          var scrollMax = panel.scrollHeight - panel.clientHeight;

          // Reading progress bar
          panel.style.setProperty('--scroll-progress', (scrollMax > 0 ? (scrollTop / scrollMax) * 100 : 0) + '%');

          if (!reducedMotion) {
            var heroParallax = Math.min(scrollTop * 0.4, 120);
            var heroOpacity = Math.max(0, 1 - scrollTop / 400);

            if (heroBgEl) heroBgEl.style.transform = 'translateY(' + heroParallax + 'px)';
            if (heroGlowEl) heroGlowEl.style.transform = 'translateY(' + (heroParallax * 0.5) + 'px)';
            if (heroInfoEl) {
              heroInfoEl.style.opacity = heroOpacity;
              heroInfoEl.style.transform = 'translateY(' + (-heroParallax * 0.2) + 'px)';
            }
            if (scrollHintEl && !scrollHintHidden) {
              scrollHintEl.style.opacity = heroOpacity * 0.4;
              if (scrollTop > 50) { scrollHintEl.style.display = 'none'; scrollHintHidden = true; }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    });

    // Close on backdrop click
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeOverlay();
    });

    // Close button
    overlay.querySelectorAll('[data-overlay-close]').forEach(function (el) {
      el.addEventListener('click', closeOverlay);
    });

    // Swipe-down-to-close (natural iOS dismissal)
    var touchStartY = 0;
    panel.addEventListener('touchstart', function (e) {
      if (panel.scrollTop === 0) touchStartY = e.touches[0].clientY;
      else touchStartY = 0;
    }, { passive: true });
    panel.addEventListener('touchend', function (e) {
      if (touchStartY && e.changedTouches[0].clientY - touchStartY > 80) closeOverlay();
      touchStartY = 0;
    }, { passive: true });

    // CTA button hover
    var ctaBtn = overlay.querySelector('.msc-partners__overlay-cta-btn');
    if (ctaBtn) {
      ctaBtn.addEventListener('mouseenter', function () {
        ctaBtn.style.transform = 'translateY(-2px)';
        ctaBtn.style.boxShadow = '0 12px 32px ' + brandColor + '40';
      });
      ctaBtn.addEventListener('mouseleave', function () {
        ctaBtn.style.transform = '';
        ctaBtn.style.boxShadow = '';
      });
    }

    // Product card hover
    panel.querySelectorAll('.msc-partners__overlay-product').forEach(function (prod) {
      prod.addEventListener('mouseenter', function () {
        prod.style.borderColor = brandColor + '30';
        prod.style.boxShadow = '0 8px 24px ' + brandColor + '10';
      });
      prod.addEventListener('mouseleave', function () {
        prod.style.borderColor = 'rgba(255,255,255,0.06)';
        prod.style.boxShadow = 'none';
      });
    });

    // Focus panel
    panel.focus();
    document.body.style.overflow = 'hidden';
    announceToSR('Viewing ' + name);
  }

  function closeOverlay() {
    if (!overlay) return;
    if (overlayObserver) { overlayObserver.disconnect(); overlayObserver = null; }
    if (activeCard) activeCard.setAttribute('aria-expanded', 'false');
    overlay.classList.remove(ACTIVE_CLS);
    if (history.replaceState) history.replaceState(null, '', window.location.pathname);
    announceToSR('Closed');

    setTimeout(function () {
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      overlay = null;
      document.body.style.overflow = '';
      if (previousFocus && previousFocus.focus) {
        previousFocus.focus();
      }
      previousFocus = null;
      activeCard = null;
    }, 600);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     3. KEYBOARD ACCESSIBILITY
     ═══════════════════════════════════════════════════════════════════════ */

  function initKeyboard() {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay) {
        e.preventDefault();
        closeOverlay();
        return;
      }

      if (e.key === 'Tab' && overlay) {
        trapFocus(e);
        return;
      }

      // Arrow key navigation within tier grids
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !overlay) {
        var focused = document.activeElement;
        if (!focused || !focused.matches(CARD_SEL)) return;
        var grid = focused.closest('[class*="msc-partners__grid--"]') || focused.closest('.msc-partners__tier');
        if (!grid) return;
        var cards = Array.prototype.slice.call(grid.querySelectorAll(CARD_SEL));
        var idx = cards.indexOf(focused);
        if (idx === -1) return;
        e.preventDefault();
        var next = e.key === 'ArrowRight'
          ? cards[(idx + 1) % cards.length]
          : cards[(idx - 1 + cards.length) % cards.length];
        next.focus();
      }
    });
  }

  function trapFocus(e) {
    var panel = overlay.querySelector('.msc-partners__overlay-panel');
    if (!panel) return;

    var focusable = panel.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]'
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || !panel.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last || !panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* 4. GSAP ScrollTrigger reveals */
  function initScrollReveal() {
    if (typeof gsap === 'undefined') {
      setTimeout(initScrollReveal, 100);
      return;
    }

    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }

    // ── Reduced motion: show everything immediately ──
    if (reducedMotion) {
      root.querySelectorAll(CARD_SEL).forEach(function (c) {
        c.style.opacity = '1';
        c.style.transform = 'none';
      });
      return;
    }

    // ── Header text reveal ──
    var headerTitle = root.querySelector('.msc-partners__header-title');
    var headerSubtitle = root.querySelector('.msc-partners__header-subtitle');

    if (headerTitle) {
      gsap.from(headerTitle, { y: 40, opacity: 0, duration: 0.8, ease: 'power3.out' });
    }
    if (headerSubtitle) {
      gsap.from(headerSubtitle, { y: 40, opacity: 0, duration: 0.8, delay: 0.15, ease: 'power3.out' });
    }

    // ── Orb parallax ──
    var orb1 = root.querySelector('.msc-partners__orb--1');
    var orb2 = root.querySelector('.msc-partners__orb--2');
    var orb3 = root.querySelector('.msc-partners__orb--3');

    if (orb1 && typeof ScrollTrigger !== 'undefined') {
      gsap.to(orb1, {
        y: -80,
        scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: 1 }
      });
    }
    if (orb2 && typeof ScrollTrigger !== 'undefined') {
      gsap.to(orb2, {
        y: -120,
        scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: 1.5 }
      });
    }
    if (orb3 && typeof ScrollTrigger !== 'undefined') {
      gsap.to(orb3, {
        y: -60,
        scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: 2 }
      });
    }

    // ── Card stagger per tier ──
    var tiers = root.querySelectorAll('.msc-partners__tier');
    tiers.forEach(function (tier) {
      var tierCards = tier.querySelectorAll(CARD_SEL);
      if (!tierCards.length) return;

      gsap.set(tierCards, { opacity: 0, y: 30 });

      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.create({
          trigger: tier,
          start: 'top 85%',
          once: true,
          onEnter: function () {
            gsap.to(tierCards, {
              opacity: 1,
              y: 0,
              duration: 0.7,
              stagger: 0.08,
              ease: 'power3.out'
            });
          }
        });
      } else {
        // Fallback if ScrollTrigger not available
        gsap.to(tierCards, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.08,
          delay: 0.3,
          ease: 'power3.out'
        });
      }
    });

    // ── CTA section reveal ──
    var ctaSection = root.querySelector('.msc-partners__page-cta');
    if (ctaSection && typeof ScrollTrigger !== 'undefined') {
      var ctaTitle = ctaSection.querySelector('.msc-partners__page-cta-title');
      var ctaText = ctaSection.querySelector('.msc-partners__page-cta-text');
      var ctaBtn = ctaSection.querySelector('.msc-partners__page-cta-btn');
      var ctaLine = ctaSection.querySelector('.msc-partners__page-cta-line');
      var ctaEls = [ctaLine, ctaTitle, ctaText, ctaBtn].filter(Boolean);

      gsap.set(ctaEls, { opacity: 0, y: 24 });

      ScrollTrigger.create({
        trigger: ctaSection,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(ctaEls, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.1,
            ease: 'power3.out'
          });
        }
      });
    }
  }

  /* 5. Ambient gradient */
  function initAmbient() {
    if (isTouch || reducedMotion) return;
    var a = document.createElement('div');
    a.className = 'msc-partners__ambient';
    root.insertBefore(a, root.firstChild);
    root.addEventListener('mouseover', function (e) {
      var c = e.target.closest(CARD_SEL);
      if (!c || c.dataset.tier === 'associate') { a.style.opacity = '0'; return; }
      var r = c.getBoundingClientRect(), p = root.getBoundingClientRect();
      a.style.cssText = 'background:radial-gradient(800px at '+(r.left+r.width/2-p.left)+'px '+(r.top+r.height/2-p.top+window.scrollY-p.top)+'px,'+(c.dataset.brandColor||'#C1282D')+'05,transparent 70%);opacity:1';
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     6. STATS COUNTER — Count-up animation for stats bars
     ═══════════════════════════════════════════════════════════════════════ */

  function initStatsCounter() {
    var containers = root.querySelectorAll('[data-partner-stats]');
    if (!containers.length) return;

    containers.forEach(function (strip) {
      var counters = strip.querySelectorAll('[data-count-to]');
      if (!counters.length) return;

      // Reduced motion or no GSAP: show final values immediately
      if (reducedMotion || typeof gsap === 'undefined') {
        for (var i = 0; i < counters.length; i++) {
          var target = parseInt(counters[i].getAttribute('data-count-to'), 10);
          if (!isNaN(target)) {
            counters[i].textContent = target;
          }
        }
        return;
      }

      function runCountUp() {
        for (var j = 0; j < counters.length; j++) {
          (function (el) {
            var target = parseInt(el.getAttribute('data-count-to'), 10);
            if (isNaN(target)) return;
            var obj = { val: 0 };
            gsap.to(obj, {
              val: target,
              duration: 1.8,
              delay: 0.2,
              ease: 'power2.out',
              onUpdate: function () {
                el.textContent = Math.round(obj.val);
              }
            });
          })(counters[j]);
        }
      }

      // If element is already in viewport (above fold), fire immediately
      var rect = strip.getBoundingClientRect();
      var inView = rect.top < window.innerHeight;

      if (inView) {
        runCountUp();
      } else if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.create({
          trigger: strip,
          start: 'top 85%',
          once: true,
          onEnter: runCountUp
        });
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     7. TIER HEADER REVEALS — Staggered ScrollTrigger entrance
     ═══════════════════════════════════════════════════════════════════════ */

  function initTierHeaders() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    if (reducedMotion) return;

    var headers = root.querySelectorAll('.msc-partners__tier-header');
    headers.forEach(function (header) {
      var children = header.children;
      if (!children.length) return;

      gsap.set(children, { opacity: 0, y: 20 });

      ScrollTrigger.create({
        trigger: header,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(children, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.06,
            ease: 'power3.out'
          });
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     8. EDITORIAL REVEAL — Staggered entrance for editorial break
     ═══════════════════════════════════════════════════════════════════════ */

  function initEditorial() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    if (reducedMotion) return;

    var editorial = root.querySelector('.msc-partners__editorial');
    if (!editorial) return;

    var children = editorial.children;
    if (!children.length) return;

    gsap.set(children, { opacity: 0, y: 20 });

    ScrollTrigger.create({
      trigger: editorial,
      start: 'top 85%',
      once: true,
      onEnter: function () {
        gsap.to(children, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: 'power3.out'
        });
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     9. COMPOUND HOVER — Brand glow opacity on card hover
     ═══════════════════════════════════════════════════════════════════════ */

  function initCompoundHover() {
    if (isTouch) return;

    root.addEventListener('mouseenter', function (e) {
      var card = e.target.closest(CARD_SEL);
      if (!card) return;
      var glow = card.querySelector('.msc-partners__card-brand-glow');
      if (glow && typeof gsap !== 'undefined') {
        gsap.to(glow, { opacity: 0.8, duration: 0.3, ease: 'power2.out' });
      }
    }, true);

    root.addEventListener('mouseleave', function (e) {
      var card = e.target.closest(CARD_SEL);
      if (!card) return;
      var glow = card.querySelector('.msc-partners__card-brand-glow');
      if (glow && typeof gsap !== 'undefined') {
        gsap.to(glow, { opacity: 0.5, duration: 0.4, ease: 'power2.out' });
      }
    }, true);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════════════════════════════════════ */

  function announceToSR(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ═══════════════════════════════════════════════════════════════════════
     BOOT
     ═══════════════════════════════════════════════════════════════════════ */

  function init() {
    initScrollReveal();
    initLightFollow();
    initAmbient();
    initExpand();
    initKeyboard();
    initStatsCounter();
    initTierHeaders();
    initEditorial();
    initCompoundHover();

    // Live region for screen reader announcements
    liveRegion = document.createElement('div');
    liveRegion.className = 'msc-partners__sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('role', 'status');
    root.appendChild(liveRegion);

    // Hide decorative "Explore →" text from assistive tech
    root.querySelectorAll('.msc-partners__card-explore').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
    });

    // Deep-link: open overlay from URL hash
    var hash = window.location.hash;
    if (hash && hash.indexOf('#partner-') === 0) {
      var targetSlug = hash.replace('#partner-', '');
      var cards = root.querySelectorAll(CARD_SEL + '[data-expand]');
      for (var i = 0; i < cards.length; i++) {
        var cardSlug = (cards[i].dataset.name || '').toLowerCase()
          .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        if (cardSlug === targetSlug) {
          (function(card) {
            setTimeout(function() { openOverlay(card); }, 800);
          })(cards[i]);
          break;
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
